import { Add as AddIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress, colors, Stack, Tooltip, Typography } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { QuickSearch } from 'components/_atoms';
import { translate as t } from 'hooks/useTranslations';
import React, { useEffect, useMemo, useCallback, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import dayjs from 'dayjs';
import { AWB } from '../../../../_types/awb';
import { useStandardsContext } from '../context';
import LeftDrawer from '_lex/_layouts/TakeoverLayout/LeftDrawer';
import Loading from 'views/App/Portal/Loading';
import PhxBadge from '_lex/_atoms/PhxBadge';
import { PhxBadgeCounter } from '_lex/_atoms/PhxBadgeCounter/PhxBadgeCounter';

interface StandardsListProps {
  selectedStandard?: string;
  setSelectedStandard?: (standardId: string) => void;
  onAddStandard?: (parentId?: string) => void;
  placeholderStandard?: {
    id: string;
    title: string;
    heading: string;
    parentStandardId?: string;
    position: number;
    standardCount: number;
    status: string;
    createdAt: string;
    updatedAt: string;
  };
  initialExpandedItems?: string[];
}

const StandardsList: React.FC<StandardsListProps> = ({
  selectedStandard: propSelectedStandard,
  setSelectedStandard: propSetSelectedStandard,
  onAddStandard: propOnAddStandard,
  placeholderStandard,
  initialExpandedItems = [],
}) => {
  const { id: accreditationId, versionId, standardId } = useParams();
  const navigate = useNavigate();
  const selectedItemRef = useRef<HTMLDivElement>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const {
    version,
    versionUpdateCounter,
    isLoading: standardsLoading,
    navigateToStandard,
    searchStandards,
    expandedItems,
    setExpandedItems,
    searchQuery,
    setSearchQuery,
    toggleExpandedItem,
    getStandardType,
  } = useStandardsContext();

  // Use props if provided, otherwise use URL params and context
  const selectedStandard = propSelectedStandard ?? standardId ?? '';
  const setSelectedStandard =
    propSetSelectedStandard ??
    ((newStandardId: string) => {
      navigate(`/admin/awb/accrediting-bodies/${accreditationId}/versions/${versionId}/standards/${newStandardId}`);
    });
  const onAddStandard =
    propOnAddStandard ??
    ((parentId?: string) => {
      if (parentId) {
        navigate(`/admin/awb/accrediting-bodies/${accreditationId}/versions/${versionId}/standards/${parentId}/new`);
      } else {
        navigate(`/admin/awb/accrediting-bodies/${accreditationId}/versions/${versionId}/standards/new`);
      }
    });

  const hasUpdates = useCallback((node: AWB.Node): boolean => {
    if (!node.lastPublishedAt || !node.updatedAt) return false;
    return dayjs(node.updatedAt).isAfter(dayjs(node.lastPublishedAt));
  }, []);

  const countUpdates = useCallback(
    (node: AWB.Node): number => {
      let count = hasUpdates(node) ? 1 : 0;

      if ('subStandards' in node && node.subStandards) {
        count += node.subStandards.reduce((sum, child) => sum + countUpdates(child), 0);
      }

      return count;
    },
    [hasUpdates]
  );

  const shouldShowChip = useCallback(
    (node: AWB.Node): boolean => {
      return hasUpdates(node);
    },
    [hasUpdates]
  );

  // Find path to selected standard
  const findPathToStandard = useCallback(
    (standards: AWB.Node[], targetId: string, currentPath: string[] = []): string[] | null => {
      for (const standard of standards) {
        if (standard.id === targetId) {
          return [...currentPath, standard.id];
        }
        if ('subStandards' in standard) {
          const path = findPathToStandard(standard.subStandards, targetId, [...currentPath, standard.id]);
          if (path) return path;
        }
      }
      return null;
    },
    []
  );

  // Get path to selected standard or placeholder
  const selectedPath = useMemo(() => {
    const targetId = selectedStandard || placeholderStandard?.id;
    if (!targetId || !version) return [];
    return findPathToStandard(version.standards, targetId) || [];
  }, [selectedStandard, placeholderStandard?.id, version, findPathToStandard, versionUpdateCounter]);

  // Auto-expand path to selected item on first load
  useEffect(() => {
    if (isFirstLoad && selectedStandard && selectedPath.length > 0 && version && !standardsLoading) {
      const pathToExpand = selectedPath.slice(0, -1);
      if (pathToExpand.length > 0) {
        const newExpandedItems = new Set([...expandedItems, ...pathToExpand]);
        setExpandedItems(Array.from(newExpandedItems));
      }
      setIsFirstLoad(false);
    }
  }, [isFirstLoad, selectedStandard, selectedPath, version, standardsLoading, expandedItems, setExpandedItems]);

  // Search functionality
  const { items: filteredStandards, paths: searchPaths } = useMemo(() => {
    if (!version) return { items: [], paths: [] };
    return searchStandards(searchQuery);
  }, [version, searchQuery, searchStandards, versionUpdateCounter]);

  // Update expanded items when search paths change
  useEffect(() => {
    if (!isEmpty(searchQuery.trim())) {
      // Reset user expanded items when searching
      setExpandedItems([]);
      const newExpandedItems = new Set<string>();
      searchPaths.forEach((path) => {
        path.forEach((id) => newExpandedItems.add(id));
      });
      setExpandedItems(Array.from(newExpandedItems));
    }
  }, [searchPaths, searchQuery, setExpandedItems]);

  // Scroll to selected item effect
  useEffect(() => {
    if (selectedStandard && selectedItemRef.current && !standardsLoading) {
      const timeoutId = setTimeout(() => {
        selectedItemRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedStandard, isFirstLoad, standardsLoading, versionUpdateCounter]);

  const handleItemClick = async (standardId: string) => {
    try {
      navigateToStandard(standardId);
      setSelectedStandard(standardId);
    } catch (error) {
      // Silent fail for navigation errors
    }
  };

  const renderStandardItem = (node: AWB.Node, level: number = 0) => {
    const itemId = node.id;
    const isPlaceholder = itemId === placeholderStandard?.id;
    const isParentOfPlaceholder = placeholderStandard?.parentStandardId === itemId;
    const nodeType = getStandardType(itemId);
    const hasChildren =
      !isPlaceholder && nodeType !== 'requirement' && (node.standardCount > 0 || isParentOfPlaceholder);
    const isSelected = itemId === selectedStandard;
    const isExpanded = expandedItems.includes(itemId);
    const showChip = !isPlaceholder && shouldShowChip(node);

    const nodeHasChildren = 'subStandards' in node && node.subStandards && node.subStandards.length > 0;
    const childrenUpdateCount = nodeHasChildren
      ? node.subStandards.reduce((sum, child) => sum + countUpdates(child), 0)
      : 0;
    const showCounter = !isPlaceholder && !isExpanded && nodeHasChildren && childrenUpdateCount > 0;

    // Create a placeholder node with required properties
    const placeholderNode = placeholderStandard
      ? (() => {
          const baseNode = {
            ...placeholderStandard,
            draftContent: '',
            publishedContent: '',
          };

          // If parent is a Standard, create a SubStandard
          if ('subStandards' in node) {
            return {
              ...baseNode,
              subStandards: [],
              requirements: [],
            } as AWB.SubStandard;
          }

          // If parent is a SubStandard, create a Requirement
          if ('requirements' in node) {
            return baseNode as AWB.Requirement;
          }

          // Default to Standard
          return {
            ...baseNode,
            subStandards: [],
          } as AWB.Standard;
        })()
      : null;

    return (
      <TreeItem
        key={itemId}
        itemId={itemId}
        label={
          <Box
            ref={isSelected ? selectedItemRef : null}
            className='phx-tree-toolbar hover-btn-container'
            onClick={() => !isPlaceholder && handleItemClick(node.id)}
            sx={{
              opacity: isPlaceholder ? 0.6 : 1,
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 2,
                width: '100%',
                alignItems: 'center',
                pr: 2,
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, overflow: 'hidden' }}>
                <Typography
                  variant='subtitleSm'
                  sx={{ color: 'slategray', flexShrink: 0 }}
                >
                  {node.heading ? node.heading + ' ' : ''}
                </Typography>
                <Typography
                  variant='subtitleSm'
                  sx={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
                >
                  {node.title}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, overflow: 'hidden', alignItems: 'center' }}>
                {showChip && (
                  <PhxBadge
                    color='secondary'
                    size='sm'
                  >
                    {t('awb.standards.list.updates')}
                  </PhxBadge>
                )}
                {showCounter && (
                  <PhxBadgeCounter
                    number={childrenUpdateCount}
                    color='primary'
                    size='sm'
                    border='rounded'
                  />
                )}
                {level <= 1 && !isPlaceholder && (
                  <Tooltip
                    title={
                      level === 1 ? t('awb.standards.list.addRequirement') : t('awb.standards.list.addSubStandard')
                    }
                    placement='bottom'
                  >
                    <Button
                      className='icon-only xs'
                      variant='dark'
                      color='neutral'
                      size='small'
                      startIcon={<AddIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddStandard(node.id);
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
        }
        sx={{
          '& .MuiTreeItem-content': {
            borderBottom: `solid 1px ${colors.grey[300]}`,
            py: 3,
          },
          '& > .MuiTreeItem-content > .MuiTreeItem-iconContainer': {
            visibility: hasChildren ? 'visible' : 'hidden',
          },
        }}
      >
        {'subStandards' in node && (
          <>
            {node.subStandards.map((child) => renderStandardItem(child, level + 1))}
            {isParentOfPlaceholder && placeholderNode && renderStandardItem(placeholderNode, level + 1)}
          </>
        )}
      </TreeItem>
    );
  };

  // Function to count total nodes in the tree
  const countNodes = (standards: AWB.Node[]): number => {
    return standards.reduce((count, standard) => {
      return count + 1 + ('subStandards' in standard ? countNodes(standard.subStandards) : 0);
    }, 0);
  };

  const headerActions = (
    <Tooltip
      title={t('awb.standards.list.addStandard')}
      placement='bottom'
    >
      <Button
        className='icon-only'
        variant='outlined'
        color='neutral'
        size='small'
        onClick={() => onAddStandard()}
        startIcon={<AddIcon />}
        disabled={false}
      />
    </Tooltip>
  );

  return (
    <LeftDrawer
      open
      header={
        <Stack
          direction='row'
          spacing={4}
          sx={{ flexGrow: 1 }}
        >
          <Typography variant='subtitle1'>{t('awb.standards.list.title')}</Typography>
        </Stack>
      }
      headerActions={headerActions}
      content={
        <Stack
          spacing={2}
          sx={{ height: '100%' }}
        >
          <Box sx={{ py: 3, px: 4, borderBottom: `solid 1px ${colors.grey[300]}` }}>
            <QuickSearch
              id='keyword_search'
              initialValue={searchQuery}
              onChange={(val) => setSearchQuery(val)}
              placeholder={'Search'}
              fullWidth
            />
          </Box>
          {standardsLoading || !version ? (
            <Loading />
          ) : (
            <SimpleTreeView
              selectedItems={selectedStandard}
              expandedItems={expandedItems}
              onItemExpansionToggle={toggleExpandedItem}
              expansionTrigger='iconContainer'
              sx={{ flex: 1, overflowY: 'auto' }}
            >
              {(searchQuery.trim() ? filteredStandards : version?.standards || []).map((standard) =>
                renderStandardItem(standard)
              )}
            </SimpleTreeView>
          )}
        </Stack>
      }
      footer={
        <Typography variant='body2'>
          {`${countNodes(searchQuery.trim() ? filteredStandards : version?.standards || [])} standards`}
        </Typography>
      }
      noPadding
    />
  );
};

export default StandardsList;
